import React from 'react';
import { Badge, Card, Space, Tabs, theme } from 'antd';
import type { TabsProps } from 'antd';

/** One tab of a {@link TabbedCountCard}: icon, label, count badge and its panel. */
export interface TabbedCountCardItem {
  key: string;
  icon: React.ReactNode;
  label: React.ReactNode;
  /** Shown in the primary badge next to the label, zero included. */
  count: number;
  children: React.ReactNode;
}

interface TabbedCountCardProps {
  items: TabbedCountCardItem[];
  activeKey: string;
  onChange: (key: string) => void;
  size?: TabsProps['size'];
}

/**
 * Flush card holding line tabs whose labels carry a count badge, used by the
 * module landing pages (e.g. applications / jobs lists).
 */
export function TabbedCountCard({ items, activeKey, onChange, size }: TabbedCountCardProps) {
  const { token } = theme.useToken();

  return (
    <Card
      styles={{ body: { padding: 0 } }}
      style={{ overflow: 'hidden', boxShadow: token.boxShadowTertiary }}
    >
      <Tabs
        activeKey={activeKey}
        onChange={onChange}
        size={size}
        type="line"
        animated
        tabBarStyle={{
          marginBottom: 0,
          paddingTop: token.paddingXS,
          paddingLeft: token.paddingMD,
          paddingRight: token.paddingMD,
          borderBottom: `${token.lineWidth}px ${token.lineType} ${token.colorBorderSecondary}`,
          background: token.colorBgContainer,
        }}
        tabBarGutter={token.marginLG}
        items={items.map((item) => ({
          key: item.key,
          label: (
            <Space size="small" style={{ padding: `0 ${token.paddingXS}px` }}>
              {item.icon}
              <span>{item.label}</span>
              <Badge count={item.count} showZero style={{ backgroundColor: token.colorPrimary }} />
            </Space>
          ),
          children: item.children,
        }))}
      />
    </Card>
  );
}

export default TabbedCountCard;
